package one.healthium.domain.user


import kotlinx.serialization.Serializable
import one.healthium.domain.common.isToday
import one.healthium.domain.health_log.models.HealthLogModel
import one.healthium.domain.user.models.FoodLogModel
import one.healthium.domain.user.models.PeriodLogModel
import one.healthium.domain.user.models.SubscriptionStatus
import one.healthium.domain.user.models.SymptomsLogModel
import one.healthium.domain.user.models.UserProfileModel
import one.healthium.domain.user.models.UserPurchasesModel
import one.healthium.domain.user.models.UserSystemModel
import one.healthium.domain.user.models.WaterLogModel
import one.healthium.domain.user.models.WeightLogModel

@Serializable
data class UserModel(
    val id: String,
    val profile: UserProfileModel,
    val purchases: UserPurchasesModel,
    val waterLogs: List<WaterLogModel>,
    val weightLogs: List<WeightLogModel>,
    val foodLogs: List<FoodLogModel>,
    val periodLogs: PeriodLogModel,
    val symptomsLogs: List<SymptomsLogModel>,
    val healthLogs: List<HealthLogModel>? = null,
    val system: UserSystemModel,
) {
    val isLocalId = id.isLocalId()

    val latestWeightLog: WeightLogModel? get() = weightLogs.firstOrNull()
    val currentWeight = latestWeightLog?.value ?: profile.weight

    val todayWaterLog: WaterLogModel? get() = waterLogs.firstOrNull { it.date.isToday() }
    val latestWaterLog: WaterLogModel? get() = waterLogs.firstOrNull()
    val dailyWaterIntake = (currentWeight ?: 0f) * 0.02f

    val todayFoodLog: FoodLogModel? get() = foodLogs.firstOrNull { it.date.isToday() }

    val todaySymptomsLog: SymptomsLogModel? get() = symptomsLogs.firstOrNull { it.date.isToday() }

    val latestHealthLog: HealthLogModel? get() = healthLogs?.firstOrNull()
    val todayHealthLog: HealthLogModel? get() = healthLogs?.firstOrNull { it.date.isToday() }

    val caloriesGoal = if (currentWeight != null && profile.height != null && profile.age != null) {
        val goal = 88.36f + (13.4f * currentWeight) + (4.8f * profile.height) - (5.7f * profile.age)
        goal.coerceIn(1500f, 3500f)
    } else {
        0f
    }
    val fatGoal = (caloriesGoal * 0.3f) / 9f
    val carbohydratesGoal = (caloriesGoal * 0.5f) / 4f
    val fiberGoal = caloriesGoal * 0.014f
    val proteinGoal = if (currentWeight != null) currentWeight * 0.8f else 0f
    val expectedDailySteps: Long = profile.age?.let { age ->
        when {
            age < 18 -> 10000
            age < 65 -> 7500
            else -> 5000
        }
    } ?: 0

    val meatPrefer = profile.additionalInfo["meat_prefer"]

    override fun toString(): String {
        return "UserModel(" +
                "id='$id', " +
                "profile=$profile, " +
                "purchases=$purchases, " +
                "waterLogs=${waterLogs.toString().take(100)}, " +
                "weightLogs=${weightLogs.toString().take(100)}, " +
                "foodLogs=${foodLogs.toString().take(100)}, " +
                "periodLogs=${periodLogs.toString().take(100)}, " +
                "symptomsLogs=${symptomsLogs.toString().take(100)}, " +
                "healthLogs=${healthLogs.toString().take(100)}, " +
                "system=$system" +
                ")"
    }

    companion object {
        internal const val localUserId = "local-user-id"

        fun createDefault(id: String = localUserId, profile: UserProfileModel? = null, purchases: UserPurchasesModel? = null) =
            UserModel(
                id = id,
                profile = profile ?: UserProfileModel.EMPTY,
                purchases = purchases ?: UserPurchasesModel(SubscriptionStatus.Inactive, null, null),
                waterLogs = emptyList(),
                weightLogs = emptyList(),
                foodLogs = emptyList(),
                periodLogs = PeriodLogModel(),
                symptomsLogs = emptyList(),
                system = UserSystemModel(0, null),
                healthLogs = emptyList(),
            )

        val preview = UserModel(
            id = "preview-user-id",
            profile = UserProfileModel.preview,
            purchases = UserPurchasesModel(SubscriptionStatus.Active, null, null),
            waterLogs = emptyList(),
            weightLogs = emptyList(),
            foodLogs = emptyList(),
            periodLogs = PeriodLogModel(),
            symptomsLogs = emptyList(),
            system = UserSystemModel(0, null),
            healthLogs = emptyList(),
        )
    }
}

fun String.isLocalId() = this == UserModel.localUserId
